import { getAuthToken, handleApiError } from './apiUtils';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1';

export interface Log {
    id: number;
    user_id: number;
    user?: {
        id: number;
        name: string;
        email: string;
    };
    module: string;
    activity: string;
    description: string;
    ip_address: string;
    user_agent: string;
    created_at: string;
    updated_at: string;
}

export interface LogsListParams {
    page?: number;
    per_page?: number;
    query?: string;
    module?: string;
    activity?: string;
    user_id?: number;
    date_from?: string;
    date_to?: string;
}

export interface LogsListResponse {
    logs: Log[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
    filters: {
        modules: string[];
        activities: string[];
    };
}

export interface LogStatistics {
    total_logs: number;
    logs_today: number;
    logs_this_week: number;
    logs_this_month: number;
    top_modules: Array<{
        module: string;
        count: number;
    }>;
    top_activities: Array<{
        activity: string;
        count: number;
    }>;
    top_users: Array<{
        user_id: number;
        user_name: string;
        count: number;
    }>;
}

export interface BackendResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

export const loggingApi = {
    // Get logs list with filtering and pagination
    getLogs: async (params: LogsListParams = {}): Promise<LogsListResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${API_BASE_URL}/logs?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<LogsListResponse> = await response.json();
        return data.data;
    },

    // Get single log details
    getLog: async (id: number): Promise<Log> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/logs/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ log: Log }> = await response.json();
        return data.data.log;
    },

    // Get logging statistics
    getStatistics: async (): Promise<LogStatistics> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/logs/statistics`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<LogStatistics> = await response.json();
        return data.data;
    },

    // Get available modules
    getModules: async (): Promise<string[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/logs/modules`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<string[]> = await response.json();
        return data.data;
    },

    // Get available activities
    getActivities: async (): Promise<string[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/logs/activities`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<string[]> = await response.json();
        return data.data;
    },
};
